import { TiendaPerfectaRouteReport } from 'models/TiendaPerfecta';

import { TiendaPerfectaService } from 'services/TiendaPerfectaService';

/**
 * Simulador de avance en vivo para demostrar el reporte.
 *
 * Trae el día REAL por el service —así usa la misma base URL y las mismas
 * cabeceras que el resto de la aplicación, y funciona igual en local que en
 * producción— y a partir de ahí va cerrando paradas. Las respuestas siguientes
 * del endpoint se interceptan y devuelven ese estado.
 *
 * La vista no se entera: recibe respuestas con la forma normal del contrato, así
 * que lo que se ve en pantalla es su comportamiento real, no una maqueta.
 *
 * `simularRutas()` abre el panel pero NO arranca: deja elegir el ritmo y avisa
 * cuántas paradas hay por cerrar antes de tocar nada.
 */

interface SimulatorContext {
  date: string;
  distributorId?: string;
}

interface SimulatorConfig {
  /** Segundos entre actualizaciones. */
  intervalo?: number;
  /** Paradas que se cierran en cada actualización. */
  paradas?: number;
}

type SimulatorState = 'loading' | 'idle' | 'running' | 'paused' | 'done';

const PANEL_ID = 'rr-simulator-panel';

let context: SimulatorContext | null = null;
let phase: SimulatorState = 'idle';
let timer: ReturnType<typeof setInterval> | null = null;
let originalXHR: typeof XMLHttpRequest | null = null;
let state: TiendaPerfectaRouteReport | null = null;
let closed = 0;
let intervalSeconds = 4;
let stepsPerTick = 2;

/**
 * Cuántos puntos separaban a la proyección del avance real al empezar.
 *
 * `projection` la calcula el servidor y acá las respuestas se interceptan, así
 * que si no se recalcula queda clavada en el valor inicial: el avance real la
 * pasa por arriba y la pantalla termina mostrando una proyección de cierre MENOR
 * que lo ya ejecutado, que es imposible.
 */
let projectionGap: number | null = null;

const panelStyles = `
  position:fixed; right:16px; bottom:16px; z-index:99999; width:272px;
  background:#0F1B2D; color:#fff; border-radius:12px; padding:16px;
  font:13px/1.5 system-ui,-apple-system,'Segoe UI',sans-serif;
  box-shadow:0 12px 40px rgba(0,0,0,.4);`;

const rowStyles =
  'display:flex; align-items:center; justify-content:space-between; gap:10px; margin-bottom:10px;';
const inputStyles = `width:64px; background:rgba(255,255,255,.12); border:1px solid rgba(255,255,255,.25);
  color:#fff; border-radius:6px; padding:4px 8px; font:inherit; text-align:right;`;
const buttonStyles = `flex:1; border:0; border-radius:7px; padding:8px 12px; font:inherit;
  font-weight:700; cursor:pointer;`;

const byId = <T extends HTMLElement>(id: string) =>
  document.getElementById(id) as T | null;

const say = (text: string) => {
  const el = byId('rr-sim-status');
  if (el) el.textContent = text;
};

const pendingStops = () =>
  state
    ? state.routes.reduce(
        (acc, route) => acc + (route.stopCount - route.finishedStopCount),
        0
      )
    : 0;

/** Refleja la fase actual en los dos botones y en los campos de ritmo. */
const paint = () => {
  const primary = byId<HTMLButtonElement>('rr-sim-primary');
  const secondary = byId<HTMLButtonElement>('rr-sim-secondary');
  if (!primary || !secondary) return;

  const labels: Record<SimulatorState, [string, string]> = {
    loading: ['Cargando…', 'Cerrar'],
    idle: ['Iniciar', 'Cerrar'],
    running: ['Pausar', 'Detener'],
    paused: ['Reanudar', 'Detener'],
    done: ['Reiniciar', 'Cerrar'],
  };

  const [primaryLabel, secondaryLabel] = labels[phase];
  primary.textContent = primaryLabel;
  secondary.textContent = secondaryLabel;

  const disabled = phase === 'loading';
  primary.disabled = disabled;
  primary.style.opacity = disabled ? '0.45' : '1';
  primary.style.cursor = disabled ? 'default' : 'pointer';

  // El ritmo se puede tocar en cualquier momento; solo mientras carga no.
  [
    byId<HTMLInputElement>('rr-sim-interval'),
    byId<HTMLInputElement>('rr-sim-steps'),
  ]
    .filter(Boolean)
    .forEach((input) => {
      (input as HTMLInputElement).disabled = disabled;
    });
};

/**
 * Cierra paradas eligiendo la ruta AL AZAR entre las que tienen pendientes.
 *
 * Avanzar siempre la más atrasada sería lo intuitivo, pero deja el timeline
 * inmóvil: esa lista se ordena justamente por paradas cerradas, así que la
 * primera sigue siendo la primera y nunca se ve un cambio de puesto. En terreno
 * las unidades avanzan en paralelo, y es ahí donde se cruzan.
 */
const advance = (times: number): number => {
  if (!state) return 0;
  let done = 0;

  for (let i = 0; i < times; i += 1) {
    const candidates = state.routes.filter(
      (r) => r.status !== 'not_started' && r.finishedStopCount < r.stopCount
    );
    const route = candidates[Math.floor(Math.random() * candidates.length)];

    if (!route) break;

    const stop = route.stops.find((s) => s.status !== 'finished');
    if (!stop) break;

    stop.status = 'finished';
    stop.startedAt = stop.startedAt || new Date().toISOString();
    stop.finishedAt = new Date().toISOString();
    route.finishedStopCount += 1;
    state.kpis.completedStops += 1;
    done += 1;
  }

  const { completedStops, scheduledStops } = state.kpis;
  const real = scheduledStops
    ? Math.round((completedStops / scheduledStops) * 100)
    : 0;

  state.progress.realProgress = real;

  // La proyección acompaña al avance conservando la distancia que tenía al
  // arrancar, y nunca queda por debajo de lo ya ejecutado. Es una aproximación
  // del simulador: en la aplicación real este número lo calcula el servidor.
  if (state.progress.projection !== null && projectionGap !== null) {
    state.progress.projection = Math.min(
      100,
      Math.max(real, real + projectionGap)
    );
  }

  return done;
};

/**
 * Intercepta únicamente `/route-report`. El resto del tráfico pasa intacto.
 *
 * El polling de la vista NO se detiene: cae acá igual que el refresco que
 * dispara el simulador, así que ambos devuelven el mismo estado y no se
 * contradicen.
 */
const interceptRequests = () => {
  if (originalXHR) return;

  originalXHR = window.XMLHttpRequest;
  const Real = originalXHR;

  function FakeXHR(this: any) {
    const xhr = new Real();
    let mocked = false;
    // Se relaja la firma a propósito: `open` y `send` tienen sobrecargas fijas
    // y acá se reenvían los argumentos tal cual llegan, sin mirarlos.
    const open = xhr.open.bind(xhr) as (...params: any[]) => void;
    const send = xhr.send.bind(xhr) as (...params: any[]) => void;

    xhr.open = (method: string, url: string, ...rest: any[]) => {
      mocked = String(url).includes('/route-report');
      return open(method, url, ...rest);
    };

    xhr.send = (...args: any[]) => {
      if (!mocked || !state) return send(...args);

      const body = JSON.stringify({ message: 'OK', error: false, data: state });
      const define = (key: string, value: unknown) =>
        Object.defineProperty(xhr, key, {
          get: () => value,
          configurable: true,
        });

      define('readyState', 4);
      define('status', 200);
      define('statusText', 'OK');
      define('responseText', body);
      define('response', body);
      xhr.getAllResponseHeaders = () => 'content-type: application/json\r\n';
      xhr.getResponseHeader = (h: string) =>
        String(h).toLowerCase() === 'content-type' ? 'application/json' : null;

      // Se disparan con eventos reales y con `this` apuntando al xhr: es lo que
      // esperan los adaptadores de axios para leer la respuesta.
      const fire = (handler: unknown, event: Event) => {
        if (typeof handler === 'function') {
          (handler as (this: XMLHttpRequest, e: Event) => void).call(
            xhr,
            event
          );
        }
      };

      setTimeout(() => {
        fire(xhr.onreadystatechange, new Event('readystatechange'));
        fire(xhr.onload, new ProgressEvent('load'));
        fire(xhr.onloadend, new ProgressEvent('loadend'));
      }, 40);

      return undefined;
    };

    return xhr;
  }

  window.XMLHttpRequest = FakeXHR as unknown as typeof XMLHttpRequest;
};

const releaseRequests = () => {
  if (originalXHR) window.XMLHttpRequest = originalXHR;
  originalXHR = null;
};

/** Usa el botón real de la vista: nada de tocar su estado por dentro. */
const triggerRefresh = () => {
  const button = document.querySelector<HTMLButtonElement>(
    '.route-report-filters__right button'
  );
  if (button) button.click();
};

const schedule = () => {
  if (timer) clearInterval(timer);

  timer = setInterval(() => {
    const done = advance(stepsPerTick);
    closed += done;

    if (!done) {
      clearInterval(timer as ReturnType<typeof setInterval>);
      timer = null;
      phase = 'done';
      paint();
      say(`Listo: ${closed} paradas cerradas. Ya no quedan pendientes.`);
      return;
    }

    triggerRefresh();
    say(`${closed} cerradas · ${state?.progress.realProgress}% de avance`);
  }, intervalSeconds * 1000);
};

/** Trae el día real. Se pide SIN interceptar, para partir de datos verdaderos. */
const loadDay = async () => {
  if (!context) return;

  phase = 'loading';
  paint();
  say('Leyendo el día…');

  releaseRequests();

  const response = await TiendaPerfectaService.getRouteReport({
    date: context.date,
    ...(context.distributorId ? { distributorId: context.distributorId } : {}),
  });

  if (!response.success) {
    phase = 'idle';
    paint();
    say('No se pudo leer el día. ¿La sesión sigue activa?');
    return;
  }

  const report = response.data as TiendaPerfectaRouteReport;

  if (!report?.routes?.length) {
    phase = 'idle';
    paint();
    say('Este día no tiene rutas cargadas. Elige otra fecha.');
    return;
  }

  state = report;
  closed = 0;
  projectionGap =
    report.progress.projection !== null
      ? report.progress.projection - report.progress.realProgress
      : null;

  const pending = pendingStops();
  phase = pending ? 'idle' : 'done';
  paint();

  say(
    pending
      ? `${pending} paradas por cerrar. Listo para iniciar.`
      : 'Todas las paradas del día ya están cerradas: no hay nada que simular.'
  );
};

/** Corta la simulación y devuelve la pantalla a los datos reales. */
const stopSimulation = (silent = false) => {
  if (timer) clearInterval(timer);
  timer = null;

  const wasIntercepting = Boolean(originalXHR);
  releaseRequests();

  // Un refresco ya sin interceptar devuelve la pantalla a datos reales sin
  // necesidad de recargar.
  if (wasIntercepting) triggerRefresh();

  if (silent) return;

  phase = 'loading';
  paint();
  loadDay();
};

/** Cierra el panel y deja todo como estaba. */
const closePanel = () => {
  stopSimulation(true);
  state = null;
  closed = 0;
  phase = 'idle';
  byId(PANEL_ID)?.remove();
};

const onPrimary = () => {
  if (phase === 'idle') {
    if (!state || !pendingStops()) return;
    interceptRequests();
    phase = 'running';
    paint();
    say(`${closed} cerradas · empezando…`);
    schedule();
    return;
  }

  if (phase === 'running') {
    if (timer) clearInterval(timer);
    timer = null;
    phase = 'paused';
    paint();
    say(`En pausa · ${closed} cerradas`);
    return;
  }

  if (phase === 'paused') {
    phase = 'running';
    paint();
    schedule();
    return;
  }

  if (phase === 'done') {
    // Volver a leer el día real y quedar listo para otra pasada.
    stopSimulation();
  }
};

const onSecondary = () => {
  if (phase === 'running' || phase === 'paused') {
    stopSimulation();
    return;
  }
  closePanel();
};

const buildPanel = () => {
  byId(PANEL_ID)?.remove();

  const panel = document.createElement('div');
  panel.id = PANEL_ID;
  panel.style.cssText = panelStyles;
  panel.innerHTML = `
    <div style="font-weight:700;margin-bottom:12px">Simulación de avance</div>

    <div style="${rowStyles}">
      <label for="rr-sim-interval">Cada</label>
      <span><input id="rr-sim-interval" type="number" min="1" max="120"
        value="${intervalSeconds}" style="${inputStyles}"> s</span>
    </div>

    <div style="${rowStyles}">
      <label for="rr-sim-steps">Paradas por vez</label>
      <input id="rr-sim-steps" type="number" min="1" max="20"
        value="${stepsPerTick}" style="${inputStyles}">
    </div>

    <div id="rr-sim-status"
      style="opacity:.85;margin:12px 0;min-height:34px">Preparando…</div>

    <div style="display:flex;gap:8px">
      <button id="rr-sim-primary"
        style="${buttonStyles}background:#fff;color:#0F1B2D">Iniciar</button>
      <button id="rr-sim-secondary"
        style="${buttonStyles}background:rgba(255,255,255,.16);color:#fff">Cerrar</button>
    </div>`;

  document.body.appendChild(panel);

  const intervalInput = byId<HTMLInputElement>('rr-sim-interval');
  const stepsInput = byId<HTMLInputElement>('rr-sim-steps');

  // La velocidad se cambia en caliente: si ya está corriendo, se reprograma sin
  // cortar la simulación.
  intervalInput?.addEventListener('change', () => {
    intervalSeconds = Math.max(1, Number(intervalInput.value) || 1);
    intervalInput.value = String(intervalSeconds);
    if (phase === 'running') schedule();
  });

  stepsInput?.addEventListener('change', () => {
    stepsPerTick = Math.max(1, Number(stepsInput.value) || 1);
    stepsInput.value = String(stepsPerTick);
  });

  byId('rr-sim-primary')?.addEventListener('click', onPrimary);
  byId('rr-sim-secondary')?.addEventListener('click', onSecondary);
};

/**
 * Abre el panel. NO arranca: deja ver cuántas paradas hay por cerrar y elegir el
 * ritmo antes de tocar nada.
 */
const open = async (config: SimulatorConfig = {}) => {
  intervalSeconds = Math.max(1, config.intervalo ?? intervalSeconds);
  stepsPerTick = Math.max(1, config.paradas ?? stepsPerTick);

  buildPanel();
  await loadDay();
};

/**
 * Deja `simularRutas()` disponible en la consola.
 *
 * Se registra desde la vista, así que solo existe mientras el reporte está
 * abierto: fuera de esta pantalla no hay nada colgando de `window`.
 */
export const installRouteReportSimulator = (ctx: SimulatorContext) => {
  context = ctx;

  const globalScope = window as unknown as Record<string, unknown>;

  globalScope.simularRutas = (config?: SimulatorConfig) => {
    open(config);
    return 'Panel abierto. Elige el ritmo y toca Iniciar.';
  };
  globalScope.detenerSimulacion = closePanel;

  return () => {
    closePanel();
    context = null;
    delete globalScope.simularRutas;
    delete globalScope.detenerSimulacion;
  };
};
